import { Component, DoCheck, EventEmitter, Input, KeyValueDiffer, KeyValueDiffers, OnInit, Output } from '@angular/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { PageService } from '@client/app/services/entities/page/page.service';
import { DirectNavigationService } from '@monsido/services/direct-navigation-service/direct-navigation.service';
import { ACCESSIBILITY_HELPER } from '@monsido/core/constants/accessibility-helper.constant';
import {
    PageDetailsSectionAccessibilityInformationFastTrackComponent,
} from '@client/app/components/pagedetails/details/sections/accessibility/information/fast-track/page-details-section-accessibility-information-fast-track.component';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';

@Component({
    selector: 'page-details-section-accessibility-information-content-component',
    standalone: false,
    template: `
        <table class="table table-fixed table-borderless">
            <tbody>
                <tr *ngFor="let error of filteredErrors; let i = index">
                    <td class="accessibility-error-image"
                        [attr.aria-label]="'Link to image' | translate"
                        [ngClass]="{'col-8': isErrorImageRelated(error), 'hidden': !isErrorImageRelated(error) }">
                        <mon-page-details-section-accessibility-image
                                [model]="error"
                                [page]="page"
                                *ngIf="isErrorImageRelated(error)" />
                    </td>
                    <td [attr.aria-label]="'HTML snippet failing check' | translate"
                        [ngClass]="{'col-40': isErrorImageRelated(error), 'col-48': !isErrorImageRelated(error) }">
                        <blockquote class="bg-danger px-0">
                            <div class="px-2 pb-2 d-flex flex-wrap">
                                <div class="mr-auto">
                                    @if (type === 'pending') {
                                        <button class="btn btn-border-radius btn-secondary mr-1"
                                                (click)="openFastTrack(error.source_code_id)"
                                                monEventTrigger="actionTrigger"
                                                [monEventParams]="{action: 'page-details-accessibility-fast-track'}"
                                                ngbTooltip="{{ 'Open Fast Track for this HTML snippet' | translate }}"
                                                container="body"
                                                triggers="focus hover">
                                            <span class="fas fa-road fa-lg fa-fw" aria-hidden="true"></span>
                                        </button>

                                        <button [attr.data-test]="'check-reviewed-button-' + error.check_id"
                                                *ngIf="check.result_type === 'review'"
                                                ngbTooltip="{{ 'Check off as Reviewed' | translate }}"
                                                container="body"
                                                (click)="onReview(error)"
                                                class="btn btn-border-radius btn-secondary mr-1">
                                            {{ 'Reviewed' | translate }}
                                        </button>

                                        <button [attr.data-test]="'check-ignore-button-' + error.check_id"
                                                *ngIf="check.result_type !== 'review' && allowIgnoreAction"
                                                ngbTooltip="{{ 'Check off as Reviewed' | translate }}"
                                                container="body"
                                                (click)="onReview(error)"
                                                class="btn btn-border-radius btn-secondary mr-1">
                                            {{ 'Ignore' | translate }}
                                        </button>

                                        <button class="btn btn-secondary"
                                                [attr.data-test]="'mark-fixed-button-' + error.check_id"
                                                monModalConfirm="{{ 'Are you sure you want to mark this issue as fixed?' | translate }}"
                                                (monModalConfirmAction)="markAsFixed(error)"
                                                ngbTooltip="{{ 'Mark this issue as fixed' | translate }}"
                                                container="body"
                                                triggers="focus hover">
                                            <span translate>Mark as fixed</span>
                                        </button>
                                    }

                                    @if (type === 'review') {
                                        <button (click)="onReview(error)"
                                                class="btn btn-border-radius btn-secondary mr-1"
                                                monEventTrigger="actionTrigger"
                                                [monEventParams]="{action: 'page-details-accessibility-remove-review'}"
                                                *ngIf="!check.ignored_on_page">
                                            {{ check.result_type == 'review' ? ('Remove review' | translate) : ('Unignore' | translate) }}
                                        </button>
                                        <button class="btn btn-secondary mr-1"
                                                monModalConfirm="{{ 'Are you sure you want to mark this check as fixed?' | translate }}"
                                                (monModalConfirmAction)="markAsFixed(error)"
                                                ngbTooltip="{{ 'Mark this issue as fixed' | translate }}"
                                                container="body"
                                                triggers="focus hover">
                                            <span translate>Mark as fixed</span>
                                        </button>
                                    }
                                </div>
                                <div>
                                    <jira-issue-buttons
                                        [error]="error"
                                        [check]="check"
                                        [page]="page"
                                        [hasJiraConnector]="hasJiraConnector">
                                    </jira-issue-buttons>

                                    <button class="btn btn-border-radius btn-secondary mr-1"
                                            triggers="focus hover"
                                            ngbTooltip="{{ 'Open issue page' | translate }}"
                                            container="body"
                                            (click)="openIssueModal(error)"
                                            monEventTrigger="actionTrigger"
                                            [monEventParams]="{action: 'open-issue-view', params: {module: 'Accessibility'}}">
                                        <span class="fas fa-info-circle" aria-hidden="true"></span>
                                    </button>

                                    <mon-extension-visual-link-button [page]="page"
                                                                  class="ml-auto"
                                                                  [type]="'acc'"
                                                                  [monDisabled]="error.isBodyReview"
                                                                  [data]="{check: check, error: error}">
                                    </mon-extension-visual-link-button>
                                </div>
                            </div>
                            <div class="px-2">
                            <span class="text-danger font-size-0-9 text-multiline">
                                {{ error.source_code | monCharacterLimit:characterLimits[i] }}
                            </span>
                                <button *ngIf="error.source_code.length > characterLimit"
                                        (click)="toggleCharacterLimit(i)"
                                        class="btn btn-border-radius btn-link">
                                <span *ngIf="characterLimits[i] === characterLimit"
                                      [attr.aria-label]="'Show the full code snippet' | translate"
                                      translate>Show more</span>
                                    <span *ngIf="characterLimits[i] !== characterLimit"
                                          [attr.aria-label]="'Show less of the code snippet' | translate"
                                          translate>Show less</span>
                                </button>
                            </div>
                        </blockquote>
                    </td>
                </tr>
            </tbody>
        </table>
    `,
})

export class PageDetailsSectionAccessibilityInformationContentComponent implements OnInit, DoCheck {
    @Input() check: any = {};
    @Input() page: any;
    @Input() type: 'pending' | 'review' = 'pending';
    @Output() updateError = new EventEmitter<any>();
    @Output() refresh = new EventEmitter<any>();

    filteredErrors: any[] = [];
    characterLimit = 150;
    characterLimits: (number | null)[] = [];
    allowIgnoreAction: boolean = false;
    checkErrorsDiffer: KeyValueDiffer<any, any>;
    hasJiraConnector: boolean = false;

    constructor (
        private monEventService: MonEventService,
        private sessionService: SessionService,
        private translateService: TranslateService,
        private monModalService: MonModalService,
        private pageService: PageService,
        private directNavigationService: DirectNavigationService,
        private keyValueDiffers: KeyValueDiffers,
    ) {
        this.checkErrorsDiffer = this.keyValueDiffers.find(this.check).create();
    }

    ngOnInit (): void {
        this.check = this.check || {};
        this.allowIgnoreAction = !this.sessionService.isDomainGroup();
        this.hasJiraConnector = this.sessionService.customer?.plan_traits?.jira_connector || false;
        this.monEventService.addListener('reloadAccessibilityIssues', () => {
            this.refresh.emit();
        });
    }

    ngDoCheck (): void {
        const changes = this.checkErrorsDiffer.diff(this.check);
        if (!changes) {
            return;
        }
        let hasCheckErrorsChanged = false;
        changes.forEachAddedItem(record => {
            if (record.key === 'errors') {
                hasCheckErrorsChanged = true;
                return;
            }
        });
        changes.forEachChangedItem(record => {
            if (record.key === 'errors') {
                hasCheckErrorsChanged = true;
                return;
            }
        });

        if (hasCheckErrorsChanged) {
            this.filteredErrors = this.filterErrors();
            this.initializeCharacterLimits();
        }
    }

    initializeCharacterLimits (): void {
        this.characterLimits = [];
        const characterLimits: number[] = [];
        this.filteredErrors.forEach((error, index) => {
            characterLimits[index] = this.characterLimit;
        });
        this.characterLimits = characterLimits;
    }

    toggleCharacterLimit (index: number): void {
        const limit = this.characterLimits[index];
        if (limit === this.characterLimit) {
            this.characterLimits[index] = null;
        } else {
            this.characterLimits[index] = this.characterLimit;
        }
    }

    filterErrors (): any[] {
        if (!this.check) {
            return [];
        }

        if (this.type === 'pending') {
            if (Array.isArray(this.check.errors)) {
                return this.check.errors.filter((error) => {
                    if (error.type === 'review' && /^<body(\s|>)/.test(error.source_code)) {
                        error.source_code = this.translateService.getString('You must perform a manual review of the page to confirm if this issue is present');
                        error.isBodyReview = true;
                    }
                    return !error.ignored;
                });
            }
            return [];
        }

        if (this.type === 'review') {
            if (this.check && Array.isArray(this.check.errors)) {
                return this.check.errors.filter((error) => {
                    return error.ignored;
                });
            }
            return [];
        }

        return [];
    }

    openFastTrack (sourceCodeId: string): void {
        const params = {
            size: 'sm',
            body: PageDetailsSectionAccessibilityInformationFastTrackComponent,
            data: { sourceCodeId: sourceCodeId },
        };
        const callback = () => {
            this.refresh.emit({});
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params, callback });
    }

    isErrorImageRelated (error: any): boolean {
        return ACCESSIBILITY_HELPER.IMG_CHECKS.indexOf(error.check_id) !== -1;
    }

    onReview (error: any): void {
        let text: string;
        let confirmText = '';
        let value: boolean;

        if (this.type === 'pending') {
            if (this.check.result_type === 'review') {
                text = this.translateService.getString('Are you sure you want to review this issue?');
                value = true;
            } else {
                text = this.translateService.getString('Are you sure you want to ignore this issue?');
                value = true;
            }
            confirmText = ' <strong>' + text + '</strong>';
            confirmText += '<br /><br />' + this.translateService.getString('Add reason (Optional)');
        }

        if (this.type === 'review') {
            if (this.check.result_type === 'review') {
                text = this.translateService.getString('Are you sure you want to remove review for this issue?');
                value = false;
            } else {
                text = this.translateService.getString('Are you sure you want to unignore this issue?');
                value = false;
            }
            confirmText = ' <strong>' + text + '</strong>';
            confirmText += '<br /><br />' + this.translateService.getString('Add reason (Optional)');
        }

        this.monModalService
            .prompt(confirmText, undefined)
            .then((comment) => {
                this.updateError.emit({ error: error, value, comment: comment });
            })
            .catch(() => {});
    }

    markAsFixed (error: any): void {
        this.pageService.deleteAccessibilityError(this.page.id, error).then(() => {
            this.refresh.emit();
        }, () => {});
    }

    openIssueModal (issue: any): void {
        this.directNavigationService.navigateToIssueOverlay({
            issueOverlayIssueId: issue.id,
            issueOverlaySourceCodeId: issue.source_code_id,
            issueOverlayPageId: this.page.id,
            issueOverlayCheckId: issue.check_id,
            issueOverlayIssueType: 'accessibility',
        });
    }

}
